import {
  API_ERROR_CODES,
  channelGateDetailsSchema,
  CLIENT_PLATFORM_HEADER,
  CLIENT_TIMEZONE_HEADER,
  CLIENT_VERSION_HEADER,
} from "@bing/shared";
import type {
  AcceptGameInviteResponse,
  ApiErrorCode,
  AvatarUploadResult,
  CreateGameInviteInput,
  DmHistoryResponse,
  DmSummaryResponse,
  DmUnreadResponse,
  FollowCounts,
  FriendLists,
  FriendView,
  GameId,
  GameInviteView,
  GateChannel,
  GamesResponse,
  TableStatusResponse,
  InboxList,
  LeaderboardPeriod,
  LeaderboardResponse,
  MatchHistoryPage,
  PromoSlot,
  PromoView,
  PublicProfile,
  SelfProfile,
  SnakeBestResponse,
  SnakeSpeed,
  SubmitReportInput,
  SubmitSnakeScoreInput,
  UpdateProfileInput,
  UsernameAvailability,
  UserStats,
} from "@bing/shared";
import { uploadWithProgress } from "./upload-with-progress";

/**
 * `FriendView` — the caller's relationship to another user — is the single
 * currency of the friends API: the accepted-friends list, the incoming/outgoing
 * pending lists, user-search hits, and the result of every mutation all speak it.
 * Re-exported so screens + the store import it from one place (alongside `Profile`).
 *
 * NOTE: `telegramId` is only meaningful in the caller's OWN view; for friend /
 * search rows it must be treated as absent and never displayed.
 */
export type { FriendView } from "@bing/shared";

/**
 * `GameInviteView` — one friend-to-private-table invite, from either party's
 * perspective — plus the accept response's `joinTarget`. Re-exported alongside
 * `FriendView` so the store/screens/tests import all social currencies from here.
 */
export type {
  AcceptGameInviteResponse,
  DmSummaryResponse,
  DmUnreadResponse,
  DmUnreadThread,
  FollowCounts,
  GameInviteView,
  /** A user's leak-free identity (no Telegram id / username) — what a `/me/dm-summary` row
   *  carries so the chat list can name a conversation without the friends list. */
  InviteUser,
} from "@bing/shared";

/**
 * `GamesResponse` — the lobby activation list from `GET /games`: each game's
 * live `status` (+ its canonical `order`) and whether the caller is an admin
 * (admins may play COMING_SOON games; the game-server enforces the same gate).
 */
export type { GameActivation, GamesResponse } from "@bing/shared";

/**
 * The caller's own profile as returned by `GET /me` — the shared `SelfProfile`
 * DTO (public fields + private Telegram fields + notifications pref + stats).
 * Aliased so the store/screens/tests keep importing `Profile` from one place.
 */
export type Profile = SelfProfile;

/** One in-progress game the user can resume (`GET /me/active-games`). The shared
 * DTO is owned by a parallel change; declared locally until it lands so the Home
 * "continue playing" strip can read the endpoint today. */
export interface ActiveGame {
  /** ITEM 1 — the saved game's own row id: a player may hold several saves of the SAME game,
   *  so this, not `game`, names one. Sent back on the join to resume exactly this entry. */
  id?: string;
  game: GameId;
  mode: string;
  roomId?: string;
  /** CHESS ONLINE RESUME — the stable `OnlineGame` id; present ⇒ a DB-backed online game the
   *  «ادامه بازی» strip resumes via `joinOrCreate("chess-online", { onlineGameId })`. */
  onlineGameId?: string;
  summary?: unknown;
  /** ITEM 1 — the settings this game was started with, so two saves of one game read
   *  differently on the strip instead of as two identical rows. */
  settings?: { difficulty?: string; players?: number; side?: number };
  updatedAt: string;
}

export interface ActiveGamesResponse {
  games: ActiveGame[];
}

export interface ApiClient {
  /** `init` is optional so the boot path can pass an `AbortSignal` for a real fetch timeout
   *  (see `resilientGetMe`). Every other caller passes nothing → unchanged. */
  getMe(init?: RequestInit): Promise<Profile>;
  updateProfile(input: UpdateProfileInput): Promise<Profile>;
  /** Live availability probe for an edited app-username (`GET /me/username-available`). */
  checkUsername(username: string): Promise<UsernameAvailability>;
  /** Toggle the notifications preference (`PATCH /me/notifications`). */
  updateNotifications(enabled: boolean): Promise<Profile>;
  /** Aggregate + per-game stats (`GET /me/stats`). */
  getStats(): Promise<UserStats>;
  /** One page of match history (`GET /me/history`); pass `cursor` for the next page. */
  getHistory(opts?: { limit?: number; cursor?: string | null }): Promise<MatchHistoryPage>;
  /** Upload a raw image (`POST /profile/avatar`); resolves to the stored photo ref.
   *  `init` carries an `AbortSignal` for the retry wrapper's per-attempt timeout. */
  uploadAvatar(data: Blob, mime: string, init?: RequestInit): Promise<AvatarUploadResult>;
  /** Remove the uploaded photo (`DELETE /profile/avatar`) — profile falls back to the preset. */
  removeAvatarPhoto(): Promise<void>;
  /** The user's resumable in-progress games (`GET /me/active-games`). */
  getActiveGames(): Promise<ActiveGamesResponse>;
  /** The lobby's game-activation list + caller admin flag (`GET /games`). */
  getGames(): Promise<GamesResponse>;
  /** What became of a table (`GET /tables/:roomId/status`) — asked when a join-by-id
   *  fails, so a stale invite link can say «بازی تمام شد» instead of guessing. */
  getTableStatus(roomId: string): Promise<TableStatusResponse>;
  /** Another user's profile view (`GET /users/:id/profile`) — public identity plus
   *  stats/history unless the owner hid them (hidden sections come back `null`). */
  getUserProfile(userId: string): Promise<PublicProfile>;

  // ── In-app inbox (@bing broadcasts) ──
  /** The caller's inbox messages + unread count (`GET /me/inbox`). */
  /** CONDITIONAL: {@link UNCHANGED} when the server answers 304 — leave the store alone. */
  getInbox(): Promise<InboxList | Unchanged>;
  /** Mark one message read (`POST /me/inbox/:id/read`). */
  markInboxRead(id: string): Promise<void>;
  /** Mark every message read (`POST /me/inbox/read-all`). */
  markAllInboxRead(): Promise<void>;
  /** Delete one message (`DELETE /me/inbox/:id`). */
  deleteInboxMessage(id: string): Promise<void>;
  /** Clear the whole inbox (`DELETE /me/inbox`). */
  clearInbox(): Promise<void>;

  // ── Friends: lists (each a list of relationship-annotated views) ──
  /** User search (`GET /users?q=`) — hits carry the caller's relationship status. */
  searchUsers(query: string): Promise<FriendView[]>;
  /** Accepted friends (`GET /friends`). */
  listFriends(): Promise<FriendView[]>;
  /** Incoming pending requests (`GET /friends/incoming`). */
  listIncoming(): Promise<FriendView[]>;
  /** Outgoing pending requests (`GET /friends/outgoing`). */
  listOutgoing(): Promise<FriendView[]>;
  /**
   * All three lists in ONE request (`GET /friends/all`) — what the ~20s poll uses.
   *
   * The three singles above are kept for the version-skew fallback below: this endpoint is
   * newer than some deployed api nodes could be during a rollout (or after an api rollback),
   * and a friends list that silently empties is far worse than one extra request.
   */
  /** CONDITIONAL: {@link UNCHANGED} when the server answers 304 — leave the store alone. */
  listFriendLists(): Promise<FriendLists | Unchanged>;
  /**
   * The «برترین‌ها» board for one (period, game).
   *
   * ⚠️ READ ON OPEN AND ON RETURNING TO THE TAB — never on a timer. The board is the same list
   * for everybody and the api caches it for 5s, but a client-side refresh would multiply by the
   * number of viewers: at prod's peak the existing 20s badge poll already runs ~10 req/s per
   * endpoint, and this screen barely changes. Freshness for the one reader who can tell — someone
   * who just finished a match — is handled server-side by the cache bypass.
   */
  leaderboard(period: LeaderboardPeriod, game: GameId | "all"): Promise<LeaderboardResponse>;
  /**
   * Everyone the caller has BLOCKED (`GET /friends/blocked`), newest first — «کاربران مسدود».
   *
   * Its own request, not part of `listFriendLists`: that one is polled every ~20s by every
   * client, and this is read when a settings screen opens. REQUIRED on the interface (not
   * optional like `createTableShare`) so a forgotten wiring is a type error rather than a
   * screen that silently shows nothing.
   */
  listBlocked(): Promise<FriendView[]>;

  // ── Friends: mutations (`POST /friends/:userId/<action>`) ──
  // `userId` is the OTHER user's id; each resolves to the caller's updated view.
  requestFriend(userId: string): Promise<FriendView>;
  /** Accept a FOLLOW deep-link: sends the friend request to the inviter behind
   * the OPAQUE `f_<ref>` (token / legacy ids resolved server-side). */
  followByRef(ref: string): Promise<FriendView>;
  /** Mint the caller's invite share card (prepared inline message) for the
   * native `shareMessage` sheet. */
  createInviteShare(): Promise<{ id: string; expirationDate: number; link?: string }>;
  /**
   * Mint the host's PRIVATE-TABLE invite (prepared inline message) for the native
   * `shareMessage` sheet — Telegram then posts the per-game copy, with the
   * `?start=t_<game>_<roomId>` deep link and NO link preview, to every chat the
   * host picks. Use `game/table-share.ts`'s `shareTableInvite` rather than
   * calling this directly (it drives the SDK and reports a fallback status).
   *
   * OPTIONAL on the interface deliberately: the test fakes build a COMPLETE
   * `ApiClient` literal (`test/helpers.tsx`, `test/session-store.test.ts`), so a
   * required member would break every one of them at once. `createHttpApiClient`
   * ALWAYS implements it; a client without it simply degrades to the copy-link /
   * `shareURL` fallback (`shareTableInvite` → `"unavailable"`), never a crash.
   */
  createTableShare?(
    game: GameId,
    roomId: string,
  ): Promise<{ id: string; expirationDate: number; link: string }>;
  acceptFriend(userId: string): Promise<FriendView>;
  rejectFriend(userId: string): Promise<FriendView>;
  cancelFriend(userId: string): Promise<FriendView>;
  removeFriend(userId: string): Promise<FriendView>;
  blockFriend(userId: string): Promise<FriendView>;
  unblockFriend(userId: string): Promise<FriendView>;
  /** Report another user (`POST /users/:id/report`). */
  reportUser(userId: string, input: SubmitReportInput): Promise<{ id: string }>;
  /**
   * Upload a report's mandatory screenshot / screen recording (`POST /reports/attachment`).
   * Goes over XHR rather than `fetch` so `onProgress` is REAL — `fetch` cannot report upload
   * progress on iOS WebKit — and so a stalled connection is abandoned while a merely slow one is
   * allowed to finish. `onProgress` receives 0–1.
   */
  uploadReportAttachment(
    data: Blob,
    mime: string,
    onProgress?: (ratio: number) => void,
  ): Promise<{ attachmentId: string }>;

  // ── Follows: DORMANT — the follow feature was removed from the UI (user
  // decision) but the api endpoints remain functional; these methods stay so
  // the surface can come back without an api-client change. Nothing calls them.
  /** Everyone the caller follows (`GET /follows`, newest first). */
  listFollowing(): Promise<FriendView[]>;
  /** Everyone following the caller (`GET /follows/followers`); each row's
   * `following` flag = whether the caller follows them BACK. */
  listFollowers(): Promise<FriendView[]>;
  /** The caller's `{followers, following}` totals (`GET /follows/counts`). */
  getFollowCounts(): Promise<FollowCounts>;
  /** Follow a user (`POST /users/:id/follow`, idempotent) → the updated view. */
  followUser(userId: string): Promise<FriendView>;
  /** Unfollow a user (`POST /users/:id/unfollow`, idempotent) → the updated view. */
  unfollowUser(userId: string): Promise<FriendView>;

  // ── Direct messages ──
  /** Per-thread unread DM counts (`GET /me/dm-unread`) — only threads with
   * unread messages, keyed by the peer's app user id. */
  getDmUnread(): Promise<DmUnreadResponse>;
  /** Per-conversation summaries (`GET /me/dm-summary`) — EVERY existing thread
   * (unread or not) with its unread count + last-message preview. */
  /** CONDITIONAL: {@link UNCHANGED} when the server answers 304 — leave the store alone. */
  getDmSummary(): Promise<DmSummaryResponse | Unchanged>;
  /**
   * Forget the ETag remembered for one conditional read, so that read's NEXT request is
   * unconditional and its body is delivered.
   *
   * ⚠️ FOR A CALLER THAT DROPS A BODY IT WAS HANDED. The tag of a 200 is remembered BEFORE the
   * caller sees the body, so a caller that then discards it — the store's epoch guard, when a
   * mutation or a push landed while the read was in flight — would otherwise leave behind the tag
   * of data it never applied, and the next read is answered 304 against it: it confirms the
   * store's local guess, not the server's data. That next read is usually the trailing run queued
   * for the very change being waited on (a friend request that never arrived; a DM preview stuck
   * on the previous message — `conditional-drop-race.test.ts`).
   *
   * Optional only so the many test fakes need not stub it: the real client always implements it,
   * and that test drives the REAL client, so a missing implementation reddens there.
   */
  forgetConditional?(path: ConditionalPath): void;
  /** One-sided «حذف گفتگو»: hide this thread from MY list only
   * (`POST /me/dm/:peerId/hide`, body-less). The peer keeps the conversation. */
  hideDmThread(peerId: string): Promise<void>;
  /**
   * One PAGE of a conversation (`GET /me/dm/:peerId/history`), oldest→newest.
   *
   * This is what the thread PAINTS FROM. `before` is a keyset cursor — the ISO timestamp of the
   * oldest message already held — so paging up never re-reads and never drifts. The DM socket
   * still carries live send/receive; it is no longer on the path to SEEING the conversation.
   */
  getDmHistory(peerId: string, before?: string): Promise<DmHistoryResponse>;

  // ── Game invites: friend → private table (60s TTL, lazily expired) ──
  /** Invite a friend to a private table (`POST /invites`); idempotent for the
   * same pending (toUserId, game, roomId). Friend-only (403 `NOT_FRIENDS`). */
  createInvite(input: CreateGameInviteInput): Promise<GameInviteView>;
  /** Accept as the invitee (`POST /invites/:id/accept`) → the invite plus the
   * `joinTarget` the client joins by room id (repeat accept = same 200). */
  acceptInvite(inviteId: string): Promise<AcceptGameInviteResponse>;
  /** Decline as the invitee (`POST /invites/:id/decline`). */
  declineInvite(inviteId: string): Promise<GameInviteView>;
  /** Withdraw as the inviter (`POST /invites/:id/cancel`). */
  cancelInvite(inviteId: string): Promise<GameInviteView>;
  /** Live (PENDING, unexpired) invites addressed to the caller, newest first. */
  listIncomingInvites(): Promise<GameInviteView[]>;
  /** Live (PENDING, unexpired) invites the caller sent, newest first. */
  listOutgoingInvites(): Promise<GameInviteView[]>;

  // ── Promos (behind the `promos` flag + an active campaign) ──
  /** The active promo for a slot (`GET /promos/:slot`), or **null** when the
   * `promos` flag is off or no campaign matches — the default today, so every
   * slot renders nothing. Cheap: one GET that returns null. */
  getPromo(slot: PromoSlot): Promise<PromoView | null>;
  /** Record that a promo was shown (`POST /promos/:id/impression`). Fire-and-forget. */
  recordPromoImpression(id: string): Promise<void>;
  /** Record a promo CTA tap (`POST /promos/:id/click`). Fire-and-forget. */
  recordPromoClick(id: string): Promise<void>;

  // ── «مار» (Snake) ──
  /** The player's best at each speed (`GET /me/snake/best`); 0 at a speed never played. */
  getSnakeBest(): Promise<SnakeBestResponse>;
  /**
   * Report a finished score AT ITS SPEED (`POST /me/snake/score`); answers the stored bests — the
   * server keeps that speed's max. Sent with `keepalive`, so a score posted as Telegram backgrounds
   * (and then kills) the app still arrives.
   */
  postSnakeScore(speed: SnakeSpeed, score: number): Promise<SnakeBestResponse>;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Stable machine-readable code from the error body (`API_ERROR_CODES`),
     * when the server supplied one — the key the UI maps to a Persian reason. */
    readonly code?: ApiErrorCode,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function isApiErrorCode(value: unknown): value is ApiErrorCode {
  return typeof value === "string" && (API_ERROR_CODES as readonly string[]).includes(value);
}

/**
 * AUTH_EXPIRED self-heal. Telegram initData embeds an `auth_date` and the api
 * rejects it past the accepted max age (401 `AUTH_EXPIRED`) — typical when the
 * Mini App webview was resumed hours later. A FULL page reload makes the
 * Telegram SDK mint fresh initData, silently fixing the session. Guards:
 *  - one-shot: the sessionStorage flag survives the reload (same tab), so if
 *    the reload did NOT fix it (initData still stale) we never reload again —
 *    no loop. The flag is cleared by the next successful response (a real
 *    recovery), re-arming the heal for a later expiry in a long-lived session;
 *  - Telegram-only: attempted only when the app actually HAD initData (see the
 *    call site) — outside Telegram a reload cannot mint credentials;
 *  - storage-less environments never reload (cannot guard against a loop).
 */
const AUTH_RELOAD_KEY = "bing:auth-expired-reload";

function reloadOnceForFreshInitData(reload: () => void): void {
  try {
    if (sessionStorage.getItem(AUTH_RELOAD_KEY) !== null) return; // already tried
    sessionStorage.setItem(AUTH_RELOAD_KEY, String(Date.now()));
  } catch {
    return;
  }
  reload();
}

function rearmAuthReload(): void {
  try {
    sessionStorage.removeItem(AUTH_RELOAD_KEY);
  } catch {
    // ignore — storage-less environments never armed it
  }
}

/**
 * The client-capture context sent as request headers (NOT in initData) for the
 * api's `data_collection`-gated analytics: the Telegram WebApp `platform` +
 * `version`, and the browser IANA timezone. Any null/undefined field sets no
 * header — guarded for non-Telegram / unresolved values.
 */
export interface ClientContext {
  platform?: string | null;
  version?: string | null;
  timezone?: string | null;
}

export interface HttpApiOptions {
  baseUrl: string;
  /**
   * MAINTENANCE (503 `MAINTENANCE`). The platform is being updated and this user is not an
   * admin. Called with the server's (admin-authored) Persian message; `active: false` fires
   * on the next SUCCESSFUL response, so turning maintenance back off restores the app on the
   * next poll with no reload. Enforced entirely server-side — this is only how the client
   * learns about it.
   */
  onMaintenance?: (state: { active: boolean; message: string }) => void;
  /**
   * CHANNELS_REQUIRED (403). The caller must join the mandatory channels before using the mini
   * app. Called with the channels to join (from the error `details`); `null` fires on the next
   * SUCCESSFUL response, so «عضو شدم» clears the gate once the user has joined. Enforced entirely
   * server-side — this is only how the client learns about it.
   */
  onChannelGate?: (channels: GateChannel[] | null) => void;
  /** Supplies the current Telegram initData for the `Authorization` header. */
  getInitData: () => string | null;
  /** Supplies the client-capture context headers (platform/version/timezone). Optional. */
  getClientContext?: () => ClientContext;
  /** Injectable fetch, for tests. */
  fetchImpl?: typeof fetch;
  /** Injectable full-page reload (the AUTH_EXPIRED self-heal), for tests. */
  reloadImpl?: () => void;
}

/**
 * «The server says nothing changed» — a **304 with no body**.
 *
 * Returned by the three badge reads (`/friends/all`, `/me/dm-summary`, `/me/inbox`) when the
 * ETag this client last saw still matches. Callers MUST treat it as «leave the store alone»,
 * not as empty data: returning `[]` here would clear a friends list on every quiet poll.
 *
 * ⚠️ WHY THE CLIENT SENDS `If-None-Match` ITSELF rather than leaning on the browser's HTTP
 * cache: these responses carry an `Authorization` header and no freshness lifetime, so whether a
 * WebView stores and revalidates them is heuristic — and a badge is not a thing to hang on a
 * heuristic. Remembering the tag here makes the 304 deterministic in every engine.
 */
export const UNCHANGED = Symbol("unchanged");
export type Unchanged = typeof UNCHANGED;

/** The three conditional reads, by the path each one's ETag is remembered under. */
export type ConditionalPath = "/friends/all" | "/me/dm-summary" | "/me/inbox";

/** Narrowing helper so call sites read as prose. */
export function isUnchanged<T>(value: T | Unchanged): value is Unchanged {
  return value === UNCHANGED;
}

export function createHttpApiClient(opts: HttpApiOptions): ApiClient {
  const doFetch = opts.fetchImpl ?? fetch;
  const doReload = opts.reloadImpl ?? ((): void => location.reload());

  /** Auth + error handling for every call. `jsonBody` sets the JSON content-type
   * (skipped for the raw-binary avatar upload, which carries its own image type) —
   * but ONLY when a body actually exists: a `content-type: application/json` header
   * on a BODY-LESS POST (friend actions, follow, invite accept/…) made Fastify
   * reject the request outright (`FST_ERR_CTP_EMPTY_JSON_BODY` → 400 VALIDATION →
   * «درخواست نامعتبر بود») — the captured root cause of the long-standing
   * add-friend failure. */
  async function send(path: string, init: RequestInit, jsonBody: boolean): Promise<Response> {
    const initData = opts.getInitData();
    const headers = new Headers(init.headers);
    if (jsonBody && init.body != null) headers.set("Content-Type", "application/json");
    if (initData) headers.set("Authorization", `tma ${initData}`);
    // Client-capture context for analytics (platform/version/timezone). Additive,
    // best-effort: each header rides every request; a null/undefined value is
    // simply omitted (so non-Telegram / unresolved fields add nothing).
    const ctx = opts.getClientContext?.();
    if (ctx?.platform) headers.set(CLIENT_PLATFORM_HEADER, ctx.platform);
    if (ctx?.version) headers.set(CLIENT_VERSION_HEADER, ctx.version);
    if (ctx?.timezone) headers.set(CLIENT_TIMEZONE_HEADER, ctx.timezone);

    const res = await doFetch(`${opts.baseUrl}${path}`, { ...init, headers });
    // ⚠️ 304 IS NOT `res.ok` — `ok` is 200-299 only. Without this the conditional reads would
    // fall into the error branch below, try to parse an empty body and throw on every quiet poll.
    // A 304 is a SUCCESS: it proves auth, maintenance and the channel gate exactly as a 200 does,
    // so it must run the same three re-arms.
    if (res.status === 304) {
      rearmAuthReload();
      opts.onMaintenance?.({ active: false, message: "" });
      opts.onChannelGate?.(null);
      return res;
    }
    if (!res.ok) {
      // Error bodies are `{ code, error, message, details? }` (see `apiErrorBodySchema`).
      const body = (await res.json().catch(() => undefined)) as
        { code?: unknown; message?: unknown; details?: unknown } | undefined;
      const code = isApiErrorCode(body?.code) ? body.code : undefined;
      const message =
        typeof body?.message === "string" && body.message ? body.message : res.statusText;
      // Stale initData self-heals with one guarded reload — only when we HAD
      // initData (i.e. running inside Telegram, where a reload gets a fresh one).
      if (code === "AUTH_EXPIRED" && initData) reloadOnceForFreshInitData(doReload);
      if (code === "MAINTENANCE") opts.onMaintenance?.({ active: true, message });
      if (code === "CHANNELS_REQUIRED") {
        const parsed = channelGateDetailsSchema.safeParse(body?.details);
        opts.onChannelGate?.(parsed.success ? parsed.data.channels : []);
      }
      throw new ApiError(res.status, message, code);
    }
    // Any success proves auth works again → re-arm the one-shot heal.
    rearmAuthReload();
    // …and proves maintenance is OVER: the app comes back on the next poll, no reload needed.
    opts.onMaintenance?.({ active: false, message: "" });
    // …and proves the channel gate is satisfied (joined) — clear it on the next good response.
    opts.onChannelGate?.(null);
    return res;
  }

  async function parse<T>(res: Response): Promise<T> {
    // 204 / empty bodies carry no JSON.
    if (res.status === 204) return undefined as T;
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  const request = async <T>(path: string, init?: RequestInit): Promise<T> =>
    parse<T>(await send(path, init ?? {}, true));

  /**
   * The last ETag this client was served per path. In memory only, and per SESSION — an app open
   * always starts by actually fetching, so a tag can never outlive the store it describes.
   */
  const etags = new Map<string, string>();

  /**
   * A conditional GET: send the last ETag, and report {@link UNCHANGED} when the server says 304.
   *
   * ⚠️ THE TAG IS STORED ONLY AFTER A BODY IS RETURNED. If parsing ever failed we would otherwise
   * remember a tag for data the store never received, and every later poll would answer UNCHANGED
   * against state that was never applied — a badge stuck for the rest of the session.
   */
  const conditional = async <T>(path: ConditionalPath): Promise<T | Unchanged> => {
    const known = etags.get(path);
    const res = await send(path, known ? { headers: { "If-None-Match": known } } : {}, true);
    if (res.status === 304) return UNCHANGED;
    const value = await parse<T>(res);
    const tag = res.headers.get("etag");
    if (tag) etags.set(path, tag);
    else etags.delete(path);
    return value;
  };

  /** The shared shape of every relationship change: `POST /friends/:id/<action>`. */
  const mutate = (userId: string, action: string): Promise<FriendView> =>
    request<FriendView>(`/friends/${encodeURIComponent(userId)}/${action}`, { method: "POST" });

  return {
    getMe: (init) => request<Profile>("/me", init),
    updateProfile: (input) =>
      request<Profile>("/me/profile", { method: "PATCH", body: JSON.stringify(input) }),
    checkUsername: (username) =>
      request<UsernameAvailability>(
        `/me/username-available?username=${encodeURIComponent(username)}`,
      ),
    updateNotifications: (enabled) =>
      request<Profile>("/me/notifications", {
        method: "PATCH",
        body: JSON.stringify({ enabled }),
      }),
    getStats: () => request<UserStats>("/me/stats"),
    getHistory: (opts) => {
      const params = new URLSearchParams();
      if (opts?.limit) params.set("limit", String(opts.limit));
      if (opts?.cursor) params.set("cursor", opts.cursor);
      const qs = params.toString();
      return request<MatchHistoryPage>(`/me/history${qs ? `?${qs}` : ""}`);
    },
    uploadAvatar: async (data, mime, init) =>
      parse<AvatarUploadResult>(
        await send(
          "/profile/avatar",
          { ...init, method: "POST", body: data, headers: { "Content-Type": mime } },
          false,
        ),
      ),
    removeAvatarPhoto: async () => {
      await send("/profile/avatar", { method: "DELETE" }, false);
    },
    getActiveGames: () => request<ActiveGamesResponse>("/me/active-games"),
    getGames: () => request<GamesResponse>("/games"),
    getTableStatus: (roomId) =>
      request<TableStatusResponse>(`/tables/${encodeURIComponent(roomId)}/status`),
    getUserProfile: (userId) =>
      request<PublicProfile>(`/users/${encodeURIComponent(userId)}/profile`),
    getInbox: () => conditional<InboxList>("/me/inbox"),
    markInboxRead: (id) =>
      request<void>(`/me/inbox/${encodeURIComponent(id)}/read`, { method: "POST" }),
    markAllInboxRead: () => request<void>("/me/inbox/read-all", { method: "POST" }),
    deleteInboxMessage: (id) =>
      request<void>(`/me/inbox/${encodeURIComponent(id)}`, { method: "DELETE" }),
    clearInbox: () => request<void>("/me/inbox", { method: "DELETE" }),

    searchUsers: (query) => request<FriendView[]>(`/users?q=${encodeURIComponent(query)}`),
    listFriends: () => request<FriendView[]>("/friends"),
    listIncoming: () => request<FriendView[]>("/friends/incoming"),
    listOutgoing: () => request<FriendView[]>("/friends/outgoing"),
    listFriendLists: () => conditional<FriendLists>("/friends/all"),
    leaderboard: (period, game) =>
      request<LeaderboardResponse>(
        `/leaderboard?period=${encodeURIComponent(period)}&game=${encodeURIComponent(game)}`,
      ),
    listBlocked: () => request<FriendView[]>("/friends/blocked"),

    requestFriend: (userId) => mutate(userId, "request"),
    followByRef: (ref) =>
      request<FriendView>(`/friends/follow-ref/${encodeURIComponent(ref)}`, { method: "POST" }),
    createInviteShare: () =>
      request<{ id: string; expirationDate: number; link?: string }>("/me/invite/prepared", {
        method: "POST",
      }),
    createTableShare: (game, roomId) =>
      request<{ id: string; expirationDate: number; link: string }>("/me/table/prepared", {
        method: "POST",
        body: JSON.stringify({ game, roomId }),
      }),
    acceptFriend: (userId) => mutate(userId, "accept"),
    rejectFriend: (userId) => mutate(userId, "reject"),
    cancelFriend: (userId) => mutate(userId, "cancel"),
    removeFriend: (userId) => mutate(userId, "remove"),
    blockFriend: (userId) => mutate(userId, "block"),
    unblockFriend: (userId) => mutate(userId, "unblock"),
    uploadReportAttachment: async (data, mime, onProgress) => {
      const initData = opts.getInitData();
      const text = await uploadWithProgress({
        url: `${opts.baseUrl}/reports/attachment`,
        body: data,
        contentType: mime,
        ...(initData ? { authorization: `tma ${initData}` } : {}),
        ...(onProgress ? { onProgress: (p) => onProgress(p.ratio) } : {}),
      });
      const parsed = JSON.parse(text) as { attachmentId?: unknown };
      if (typeof parsed.attachmentId !== "string" || !parsed.attachmentId) {
        throw new Error("attachment upload returned no id");
      }
      return { attachmentId: parsed.attachmentId };
    },
    reportUser: (userId, input) =>
      request<{ id: string }>(`/users/${encodeURIComponent(userId)}/report`, {
        method: "POST",
        body: JSON.stringify(input),
      }),

    listFollowing: () => request<FriendView[]>("/follows"),
    listFollowers: () => request<FriendView[]>("/follows/followers"),
    getFollowCounts: () => request<FollowCounts>("/follows/counts"),
    followUser: (userId) =>
      request<FriendView>(`/users/${encodeURIComponent(userId)}/follow`, { method: "POST" }),
    unfollowUser: (userId) =>
      request<FriendView>(`/users/${encodeURIComponent(userId)}/unfollow`, { method: "POST" }),

    getDmUnread: () => request<DmUnreadResponse>("/me/dm-unread"),
    getDmSummary: () => conditional<DmSummaryResponse>("/me/dm-summary"),
    forgetConditional: (path) => {
      etags.delete(path);
    },
    // Body-less POST → `send` sets no JSON content-type (Fastify rejects it on an
    // empty body). Same shape as the friend/inbox body-less POSTs.
    hideDmThread: (peerId) =>
      request<void>(`/me/dm/${encodeURIComponent(peerId)}/hide`, { method: "POST" }),
    getDmHistory: (peerId, before) =>
      request<DmHistoryResponse>(
        `/me/dm/${encodeURIComponent(peerId)}/history` +
          (before ? `?before=${encodeURIComponent(before)}` : ""),
      ),

    createInvite: (input) =>
      request<GameInviteView>("/invites", { method: "POST", body: JSON.stringify(input) }),
    acceptInvite: (inviteId) =>
      request<AcceptGameInviteResponse>(`/invites/${encodeURIComponent(inviteId)}/accept`, {
        method: "POST",
      }),
    declineInvite: (inviteId) =>
      request<GameInviteView>(`/invites/${encodeURIComponent(inviteId)}/decline`, {
        method: "POST",
      }),
    cancelInvite: (inviteId) =>
      request<GameInviteView>(`/invites/${encodeURIComponent(inviteId)}/cancel`, {
        method: "POST",
      }),
    listIncomingInvites: () => request<GameInviteView[]>("/invites/incoming"),
    listOutgoingInvites: () => request<GameInviteView[]>("/invites/outgoing"),

    getPromo: (slot) => request<PromoView | null>(`/promos/${encodeURIComponent(slot)}`),
    recordPromoImpression: async (id) => {
      await send(`/promos/${encodeURIComponent(id)}/impression`, { method: "POST" }, false);
    },
    recordPromoClick: async (id) => {
      await send(`/promos/${encodeURIComponent(id)}/click`, { method: "POST" }, false);
    },

    getSnakeBest: () => request<SnakeBestResponse>("/me/snake/best"),
    postSnakeScore: (speed, score) =>
      request<SnakeBestResponse>("/me/snake/score", {
        method: "POST",
        body: JSON.stringify({ speed, score } satisfies SubmitSnakeScoreInput),
        keepalive: true,
      }),
  };
}
